// Note: This uses Puppeteer which requires a Node.js environment
// For client-side usage, consider using a server endpoint

import { API_ENDPOINTS } from "../api/apiEndpoints";

// For client-side usage, we'll use the test server endpoint or proxy
const API_BASE_URL = '';

// Helper function to clean markdown text
function cleanMarkdown(text) {
    if (!text) return 'No data available';
    return text
        .replace(/#{1,6}\s/g, '')
        .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
        .replace(/\*(.*?)\*/g, '<em>$1</em>')
        .replace(/\n/g, '<br>')
        .trim();
}

// Helper function to format numbers
function formatNumber(num) {
    if (typeof num === 'number') {
        return num.toLocaleString('en-IN');
    }
    return num || 'N/A';
}

// Helper function to generate table HTML
function generateTable(data, headers = null) {
    if (!data || !Array.isArray(data) || data.length === 0) {
        return '<div class="no-data">No data available</div>';
    }

    // Get headers from data if not provided
    if (!headers) {
        headers = Object.keys(data[0]);
    }

    if (headers.length === 0) return '<div class="no-data">No data available</div>';

    let tableHTML = '<table class="table"><thead><tr>';
    
    // Generate header row
    headers.forEach(header => {
        const displayHeader = header.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
        tableHTML += `<th>${displayHeader}</th>`;
    });
    
    tableHTML += '</tr></thead><tbody>';
    
    // Generate data rows
    data.forEach(row => {
        tableHTML += '<tr>';
        headers.forEach(header => {
            const value = row[header];
            const formattedValue = typeof value === 'number' ? formatNumber(value) : (value || 'N/A');
            const cellClass = typeof value === 'number' ? 'number-format' : '';
            tableHTML += `<td class="${cellClass}">${formattedValue}</td>`;
        });
        tableHTML += '</tr>';
    });
    
    tableHTML += '</tbody></table>';
    return tableHTML;
}

// Function to generate HTML content from JSON data
function generateHTMLContent(data, customerName = 'Test Customer', location = '') {
    // Read the HTML template (this would need to be bundled or served differently in production)
    const htmlTemplate = `<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Focus360 Report</title>
    <style>
        @page {
            size: A4;
            margin: 15mm;
        }
        
        body {
            font-family: 'Arial', sans-serif;
            line-height: 1.4;
            color: #333;
            font-size: 12px;
            margin: 0;
            padding: 0;
        }
        
        .header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 20px;
            border-bottom: 2px solid #e72020;
            padding-bottom: 10px;
        }
        
        .logo {
            height: 40px;
        }
        
        .title-banner {
            background-color: #e72020;
            color: white;
            text-align: center;
            padding: 12px;
            margin: 20px 0;
            font-size: 16px;
            font-weight: bold;
        }
        
        .generated-date {
            text-align: right;
            margin-bottom: 15px;
            font-size: 10px;
            color: #666;
        }
        
        .section {
            margin-bottom: 25px;
            page-break-inside: avoid;
        }
        
        .section-heading {
            font-size: 14px;
            font-weight: bold;
            color: #e72020;
            margin-bottom: 12px;
            border-bottom: 1px solid #ddd;
            padding-bottom: 4px;
        }
        
        .subsection-heading {
            font-size: 12px;
            font-weight: bold;
            color: #666;
            margin: 15px 0 8px 0;
        }
        
        .content {
            margin-bottom: 10px;
            text-align: justify;
        }
        
        .no-data {
            font-style: italic;
            color: #888;
            margin: 10px 0;
        }
        
        .table {
            width: 100%;
            border-collapse: collapse;
            margin: 15px 0;
            font-size: 10px;
        }
        
        .table th {
            background-color: #e72020;
            color: white;
            padding: 8px;
            text-align: left;
            border: 1px solid #ccc;
            font-weight: bold;
        }
        
        .table td {
            padding: 6px 8px;
            border: 1px solid #ccc;
            text-align: left;
        }
        
        .table tbody tr:nth-child(even) {
            background-color: #f9f9f9;
        }
        
        .chart-container {
            text-align: center;
            margin: 15px 0;
        }
        
        .chart-image {
            max-width: 100%;
            height: auto;
            max-height: 300px;
        }
        
        .page-break {
            page-break-before: always;
        }

        /* No fixed footers: they overlapped content. Browser preview uses doc-end note only. */
        
        .number-format {
            text-align: right;
        }
        
        .summary-text {
            white-space: pre-line;
            line-height: 1.5;
        }
    </style>
</head>
<body>
    <!-- Header -->
    <div class="header">
        <div style="font-weight: bold; color: #e72020;">ACG Logo</div>
        <div style="font-weight: bold; color: #0055ff;">Focus360 Logo</div>
    </div>

    <div class="generated-date">
        <strong>Customer:</strong> {{CUSTOMER_NAME}} | <strong>Location:</strong> {{LOCATION}} | <strong>Generated Date:</strong> {{GENERATED_DATE}}
    </div>

    <div class="title-banner">
        Focus360 Insights Report
    </div>

    <!-- Page 1: Main Analysis -->
    
    <!-- 1. Pending Payments Section -->
    <div class="section">
        <div class="section-heading">1. Pending Payments Analysis</div>
        
        <!-- 1.1 Aging Table -->
        <div class="subsection-heading">1.1 Aging Analysis Table</div>
        {{AGING_TABLE_SECTION}}
        
        <!-- 1.2 Pie Chart -->
        <div class="subsection-heading">1.2 Payment Distribution Chart</div>
        {{CHART_SECTION}}
        
        <!-- 1.3 Summary -->
        <div class="subsection-heading">1.3 Payment Summary</div>
        {{PAYMENT_SUMMARY_SECTION}}
    </div>

    <!-- 2. Dispatch Status Section -->
    <div class="section">
        <div class="section-heading">2. Dispatch Status Analysis</div>
        
        <!-- 2.1 Summary -->
        <div class="subsection-heading">2.1 Dispatch Summary</div>
        {{DISPATCH_SUMMARY_SECTION}}
    </div>

    <!-- 3. External Insights Section -->
    <div class="section">
        <div class="section-heading">3. External Market Insights</div>
        {{EXTERNAL_INSIGHTS_SECTION}}
    </div>

    <!-- Page Break for Detailed Tables -->
    <div class="page-break"></div>

    <!-- Header for Page 2 -->
    <div class="header">
        <div style="font-weight: bold; color: #e72020;">ACG Logo</div>
        <div style="font-weight: bold; color: #0055ff;">Focus360 Logo</div>
    </div>

    <div class="title-banner">
        Detailed Analysis Tables
    </div>

    <!-- 4. Dispatch Status Details -->
    <div class="section">
        <div class="section-heading">4. Dispatch Status Details</div>
        {{DISPATCH_TABLE_SECTION}}
    </div>

    <!-- 5. Detailed Due Payments -->
    <div class="section">
        <div class="section-heading">5. Detailed Pending Payments - Due</div>
        {{DUE_TABLE_SECTION}}
    </div>

    <!-- 6. Detailed Credit Payments -->
    <div class="section">
        <div class="section-heading">6. Detailed Pending Payments - Credit</div>
        {{CREDIT_TABLE_SECTION}}
    </div>

    <!-- 7. Site information -->
    <div class="section">
        <div class="section-heading">7. Site information</div>
        {{SITE_INFO_TABLE_SECTION}}
    </div>

    <div class="doc-end-note" style="margin-top:28px;padding-top:14px;font-size:9px;color:#666;text-align:center;border-top:1px solid #ddd;">
        Confidential Report © ACG Group
    </div>

</body>
</html>`;
    
    const focusData = data.data || data;
    const currentDate = new Date().toLocaleDateString('en-GB');

    const resolvedCustomerName =
        (typeof customerName === 'string' && customerName.trim()) ||
        (typeof data?.customer_name === 'string' && data.customer_name.trim()) ||
        (typeof focusData?.customer_name === 'string' && focusData.customer_name.trim()) ||
        'Focus360 Customer';

    const resolvedLocation =
        (typeof location === 'string' && location.trim()) ||
        (typeof data?.location === 'string' && data.location.trim()) ||
        (typeof focusData?.location === 'string' && focusData.location.trim()) ||
        '';

    let processedHTML = htmlTemplate;

    processedHTML = processedHTML.replace('{{CUSTOMER_NAME}}', resolvedCustomerName);
    processedHTML = processedHTML.replace('{{LOCATION}}', resolvedLocation || '—');
    processedHTML = processedHTML.replace('{{GENERATED_DATE}}', currentDate);
    
    // 1.1 Aging Table
    const agingTable = focusData.pending_payments?.pending_payments_aging_table;
    const agingTableHTML = generateTable(agingTable);
    processedHTML = processedHTML.replace('{{AGING_TABLE_SECTION}}', agingTableHTML);
    
    // 1.2 Chart Section
    const chartUrl = focusData.pending_payments?.pending_payments_pie_chart_url;
    let chartHTML;
    if (chartUrl) {
        chartHTML = `<div class="chart-container">
            <img src="${chartUrl}" alt="Payment Distribution Chart" class="chart-image">
        </div>`;
    } else {
        chartHTML = '<div class="no-data">No chart data available</div>';
    }
    processedHTML = processedHTML.replace('{{CHART_SECTION}}', chartHTML);
    
    // 1.3 Payment Summary
    const paymentSummary = focusData.pending_payments?.pending_payments_summary;
    const paymentSummaryHTML = paymentSummary 
        ? `<div class="content summary-text">${cleanMarkdown(paymentSummary)}</div>`
        : '<div class="no-data">No payment summary available</div>';
    processedHTML = processedHTML.replace('{{PAYMENT_SUMMARY_SECTION}}', paymentSummaryHTML);
    
    // 2.1 Dispatch Summary
    const dispatchSummary = focusData.dispatch_status?.dispatch_status_summary;
    const dispatchSummaryHTML = dispatchSummary 
        ? `<div class="content summary-text">${cleanMarkdown(dispatchSummary)}</div>`
        : '<div class="no-data">No dispatch summary available</div>';
    processedHTML = processedHTML.replace('{{DISPATCH_SUMMARY_SECTION}}', dispatchSummaryHTML);
    
    // 2.2 Dispatch Table (moved to detailed section)
    const dispatchTable = focusData.dispatch_status?.dispatch_status_table;
    const dispatchTableHTML = generateTable(dispatchTable);
    processedHTML = processedHTML.replace('{{DISPATCH_TABLE_SECTION}}', dispatchTableHTML);
    
    // 3. External Insights
    const externalInsights = focusData.external?.external_insights;
    const externalInsightsHTML = externalInsights 
        ? `<div class="content summary-text">${cleanMarkdown(externalInsights)}</div>`
        : '<div class="no-data">No external insights available</div>';
    processedHTML = processedHTML.replace('{{EXTERNAL_INSIGHTS_SECTION}}', externalInsightsHTML);
    
    // 4. Detailed Due Table
    const dueTable = focusData.pending_payments?.pending_payments_detailed_table_due;
    const dueTableHTML = generateTable(dueTable);
    processedHTML = processedHTML.replace('{{DUE_TABLE_SECTION}}', dueTableHTML);
    
    // 5. Detailed Credit Table
    const creditTable = focusData.pending_payments?.pending_payments_detailed_table_credit;
    const creditTableHTML = generateTable(creditTable);
    processedHTML = processedHTML.replace('{{CREDIT_TABLE_SECTION}}', creditTableHTML);

    const siteInfoHeaders = ['SiteUID', 'SiteAccount_Name', 'Address', 'Country', 'City'];
    const siteInfoRows = Array.isArray(data?.site_info) && data.site_info.length > 0 ? data.site_info : null;
    const siteInfoTableHTML = siteInfoRows
        ? generateTable(siteInfoRows, siteInfoHeaders)
        : '<div class="no-data">No site information available</div>';
    processedHTML = processedHTML.replace('{{SITE_INFO_TABLE_SECTION}}', siteInfoTableHTML);

    // #region agent log
    try {
        const footerDivCount = (processedHTML.match(/class="footer"/g) || []).length;
        fetch('http://127.0.0.1:7567/ingest/a4349dfc-c6f2-4f29-98e9-fd0f26d159d4', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'X-Debug-Session-Id': '7636f9' },
            body: JSON.stringify({
                sessionId: '7636f9',
                runId: 'post-fix',
                hypothesisId: 'H5',
                location: 'pdfGenerator.js:generateHTMLContent',
                message: 'client pdf html footer audit',
                data: {
                    footerDivCount,
                    hasHardcodedPage1: processedHTML.includes('Page 1 |'),
                    footerCssFixed: /\.footer\s*\{[^}]*position:\s*fixed/s.test(processedHTML),
                },
                timestamp: Date.now(),
            }),
        }).catch(() => {});
    } catch (_) {}
    // #endregion

    return processedHTML;
}

// Client-side PDF generation function (fallback)
export const generateFocus360PDF = async (apiResponse, customerName, location = '') => {
    try {
        console.log('🚀 Starting PDF generation request...');
        console.log('📊 Data to send:', apiResponse);
        console.log('👤 Customer name:', customerName);
        console.log('📍 Location:', location);

        const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.generatePDF}`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                data: apiResponse,
                customerName,
                location,
            })
        });
        
        console.log('📡 Response status:', response.status);
        console.log('📄 Response headers:', response.headers);

        if (response.ok) {
            // Parse JSON response containing blob URL
            const result = await response.json();
            console.log('📥 Response received:', result);
            
            if (result.success && result.downloadUrl) {
                console.log('🔗 Download URL:', result.downloadUrl);
                
                // Create download link using Azure blob URL
                const link = document.createElement('a');
                link.href = result.downloadUrl;
                link.download = result.filename;
                link.target = '_blank'; // Open in new tab to avoid about:blank issue
                
                // Trigger download
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                
                console.log('✅ PDF download initiated successfully');
            } else {
                throw new Error(result.message || 'PDF generation failed - no download URL received');
            }
        } else {
            // Try to parse error message
            try {
                const error = await response.json();
                throw new Error(error.message || 'Failed to generate PDF');
            } catch {
                throw new Error(`Server error: ${response.status} ${response.statusText}`);
            }
        }
        
    } catch (error) {
        console.error('Error generating PDF:', error);
        
        // Fallback: Generate HTML content and show in new window
        try {
            const htmlContent = generateHTMLContent(apiResponse, customerName, location);
            const newWindow = window.open('', '_blank');
            if (newWindow) {
                newWindow.document.write(htmlContent);
                newWindow.document.close();
                console.log('PDF generation failed, opened HTML preview instead');
            }
        } catch (fallbackError) {
            console.error('Fallback also failed:', fallbackError);
            alert('Failed to generate PDF. Please try again.');
        }
        
        throw error;
    }
};

// Export additional functions for server-side usage
export {
    generateHTMLContent,
    cleanMarkdown,
    formatNumber,
    generateTable
};